import React, { useContext } from 'react';
import moment from 'moment';
import { LatestDeviceImagesContext } from '../../../../__state/LatestDeviceImages';
import { doesWeatherValueExist, getWeather } from '../../../../utils/functions';
import config from '../../../../utils/config';

import WindCompass from '../../../../components/WindCompass';
import LightningRadar, { LightningLegend } from '../../../../components/LightningRadar';
import WeatherStatusIcons from '../../../../components/WeatherStatusIcons';

moment.tz.setDefault('UTC');

const MWSDashboard = ({
	device,
	latestEntry,
	isLightningActive,
	isRainActive,
	isCloudsActive,
	isTamperingActive,
	batteryLevel,
	showMetricOnly,
	theme = 'dark',
}) => {
	const { deviceImages } = useContext(LatestDeviceImagesContext);
	const imageBaseURL = config['MWS']['constants']['imageURL'];
	const latestEntryImages = deviceImages[device.imei];
	const latestImages = latestEntryImages && latestEntryImages.images ? latestEntryImages.images : [];

	// Helper function
	const getValue = (field, suffix = '') => {
		if (!doesWeatherValueExist(field)) return '-';
		return `${field.value}${suffix}`;
	};

	// Parse numeric value
	const getNumericValue = (field) => {
		if (!doesWeatherValueExist(field)) return null;
		const val = parseFloat(field.value);
		return isNaN(val) ? null : val;
	};

	// Temperature data
	const tempC = getNumericValue(latestEntry.temperature);
	const tempF = getNumericValue(latestEntry.temperatureF);
	const dewC = getNumericValue(latestEntry.dewPoint);
	const dewF = getNumericValue(latestEntry.dewPointF);
	const humidity = getValue(latestEntry.humidity, '%');

	// Wind data
	const windDir = getNumericValue(latestEntry.windDir);
	const windSpeedKt = latestEntry.windSpeedMph?.value
		? (parseFloat(latestEntry.windSpeedMph.value) * 0.868976).toFixed(1) + ' kt'
		: '-';
	const maxWindDir = getValue(latestEntry.maxWindDir, '°');
	const maxWindSpeedKt = latestEntry.maxWindSpeedMph?.value
		? (parseFloat(latestEntry.maxWindSpeedMph.value) * 0.868976).toFixed(1) + ' kt'
		: '-';

	// Pressure data
	const stationPressure = getValue(latestEntry.stationPressure, ' inHg');
	const stationPressureMb = getValue(latestEntry.stationPressureMb, ' hPa');

	// Lightning data
	const lightningFreq = getNumericValue(latestEntry.lightningFreq) || 0;
	const lightningDist = getNumericValue(latestEntry.lightningDist);

	// Weather data
	const presentWeatherText = doesWeatherValueExist(latestEntry.weather)
		? getWeather(latestEntry.weather)
		: '-';
	const precipRate = showMetricOnly
		? getValue(latestEntry.precipMm, ' mm/hr')
		: getValue(latestEntry.precip, ' in/hr');

	// Cloud layers - parse into 6 individual layer values
	const parseCloudLayers = () => {
		const layers = ['-', '-', '-', '-', '-', '-'];
		if (!doesWeatherValueExist(latestEntry.cloudLayers)) return layers;

		const value = latestEntry.cloudLayers.value;
		// Handle special cases
		if (value === 'NCD' || value === 'CLR' || value === 'No Ceilometer' || value === 'Ceilometer OFF') {
			return layers;
		}

		// Parse comma-separated values (MWS uses feet directly)
		const layerValues = value.split(',').map(v => v.trim()).filter(v => v);
		layerValues.forEach((layerFt, index) => {
			if (index < 6) {
				const feet = parseFloat(layerFt);
				if (!isNaN(feet)) {
					layers[index] = `${Math.round(feet)} ft`;
				}
			}
		});
		return layers;
	};
	const cloudLayerValues = parseCloudLayers();

	// Image helper
	const imageTimestamp = (imageName) => {
		try {
			let imageTs = '20' + imageName.split('-')[1];
			imageTs = imageTs.slice(0, imageTs.length - 4) + 'T' + imageTs.slice(imageTs.length - 4);
			return moment(imageTs).format('YYYY-MM-DD HH:mm:ss');
		} catch {
			return '';
		}
	};

	const calculateOrientation = (orientation, multiplier) => {
		const finalO = orientation + multiplier * 90;
		return finalO > 360 ? finalO - 360 : finalO;
	};

	return (
		<div className="dashboard-grid">
			{/* LEFT COLUMN */}
			{/* Timestamp Section */}
			<div className="dashboard-section timestamp-section">
				<div className="section-header">Last Observation</div>
				<div className="section-content timestamp-content">
					<div className="timestamp-row">
						<span className="timestamp-label">PC Time (UTC)</span>
						<span className="timestamp-value">
							{moment().utc().format('YY-MM-DD HH:mm:ss')}
						</span>
					</div>
					<div className="timestamp-row">
						<span className="timestamp-label">Device</span>
						<span className="timestamp-value">
							{moment(latestEntry.timestamp).format('YY-MM-DD HH:mm:ss')}
						</span>
					</div>
				</div>
			</div>

			{/* Status Section */}
			<div className="dashboard-section status-section">
				<div className="section-header">Status</div>
				<div className="section-content">
					<WeatherStatusIcons
						theme={theme}
						isRainActive={isRainActive}
						isLightningActive={isLightningActive}
						isTamperingActive={isTamperingActive}
						isCameraActive={latestImages.length > 0}
						batteryLevel={batteryLevel}
					/>
				</div>
			</div>

			{/* CENTER COLUMN */}
			{/* Wind Section */}
			<div className="dashboard-section wind-section">
				<div className="section-header">Wind</div>
				<div className="section-content wind-content">
					{/* Top row: Compass + Current + Max */}
					<div className="wind-top-row">
						<div className="wind-compass-area">
							<WindCompass
								direction={windDir}
								theme={theme}
								size={160}
							/>
						</div>
						<div className="wind-current">
							<h4>Current</h4>
							<table className="dashboard-table">
								<tbody>
									<tr>
										<td>Direction</td>
										<td className="value-cell">{windDir !== null ? windDir : '-'}</td>
										<td className="unit-cell">°</td>
									</tr>
									<tr>
										<td>Speed</td>
										<td className="value-cell">{windSpeedKt.replace(' kt', '')}</td>
										<td className="unit-cell">kt</td>
									</tr>
								</tbody>
							</table>
						</div>
						<div className="wind-max">
							<h4>Max</h4>
							<table className="dashboard-table">
								<tbody>
									<tr>
										<td>Direction</td>
										<td className="value-cell">{maxWindDir.replace('°', '')}</td>
										<td className="unit-cell">°</td>
									</tr>
									<tr>
										<td>Speed</td>
										<td className="value-cell">{maxWindSpeedKt.replace(' kt', '')}</td>
										<td className="unit-cell">kt</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>

			{/* Lightning Section */}
			<div className="dashboard-section lightning-section">
				<div className="section-header">Lightning</div>
				<div className="section-content lightning-content">
					<div className="lightning-radar-area">
						<LightningRadar
							theme={theme}
							size={160}
							strikeCount1min={lightningFreq}
							strikeDist1min={lightningDist}
						/>
					</div>
					<div className="lightning-legend-area">
						<LightningLegend />
						<p className="lightning-range-text">Range Rings: 5, 10, 25, 50 NM</p>
					</div>
					<div className="lightning-data-area">
						<table className="dashboard-table">
							<thead>
								<tr>
									<th></th>
									<th>Strike Count</th>
									<th>Min Distance</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>1 Min</td>
									<td className="value-cell">{lightningFreq}</td>
									<td className="value-cell">{lightningDist !== null ? `${lightningDist} NM` : '-'}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Weather & Clouds Row (side by side) */}
			<div className="weather-clouds-row">
				{/* Weather Section */}
				<div className="dashboard-section weather-section">
					<div className="section-header">Weather</div>
					<div className="section-content">
						<div className="card-rows">
							<div className="card-row present-weather-row">
								<span className="row-label">Present Weather</span>
								<span className="row-value">{presentWeatherText}</span>
							</div>
							<div className="card-row">
								<span className="row-label">Precipitation Rate</span>
								<span className="row-value">{precipRate}</span>
							</div>
						</div>
					</div>
				</div>

				{/* Clouds Section */}
				<div className="dashboard-section clouds-section">
					<div className="section-header">Clouds</div>
					<div className="section-content">
						<div className="card-rows">
							<div className="card-row">
								<span className="row-label">Layer 1</span>
								<span className="row-value">{cloudLayerValues[0]}</span>
							</div>
							<div className="card-row">
								<span className="row-label">Layer 2</span>
								<span className="row-value">{cloudLayerValues[1]}</span>
							</div>
							<div className="card-row">
								<span className="row-label">Layer 3</span>
								<span className="row-value">{cloudLayerValues[2]}</span>
							</div>
							<div className="card-row">
								<span className="row-label">Layer 4</span>
								<span className="row-value">{cloudLayerValues[3]}</span>
							</div>
							<div className="card-row">
								<span className="row-label">Layer 5</span>
								<span className="row-value">{cloudLayerValues[4]}</span>
							</div>
							<div className="card-row">
								<span className="row-label">Layer 6</span>
								<span className="row-value">{cloudLayerValues[5]}</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Camera Images Section (MWS specific) */}
			{latestImages.length > 0 && (
				<div className="dashboard-section camera-section">
					<div className="section-header">Camera</div>
					<div className="section-content">
						<div className="camera-grid">
							{latestImages.map((imageSrc, i) => {
								const orientation = latestEntryImages?.initialImgOrientation;
								return (
									<div key={i} className="camera-image">
										<img alt={`Camera ${i + 1}`} src={`${imageBaseURL}/${device.imei}/${imageSrc}`} />
										{orientation && (
											<span className="camera-info">
												Camera {i + 1} ({calculateOrientation(parseInt(orientation), i)}°)
												<br />
												{imageTimestamp(imageSrc)}
											</span>
										)}
									</div>
								);
							})}
						</div>
					</div>
				</div>
			)}

			{/* RIGHT COLUMN */}
			{/* Temperature Section */}
			<div className="dashboard-section temperature-section">
				<div className="section-header">Temperature</div>
				<div className="section-content">
					<div className="table-responsive">
						<table className="dashboard-table">
							<thead>
								<tr>
									<th></th>
									<th>Value</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>Temperature</td>
									<td className="value-cell">{showMetricOnly ? (tempC ?? '-') : (tempF ?? '-')}</td>
									<td className="unit-cell">{showMetricOnly ? '°C' : '°F'}</td>
								</tr>
								<tr>
									<td>Dew Point</td>
									<td className="value-cell">{showMetricOnly ? (dewC ?? '-') : (dewF ?? '-')}</td>
									<td className="unit-cell">{showMetricOnly ? '°C' : '°F'}</td>
								</tr>
								<tr>
									<td>Rel. Humidity</td>
									<td className="value-cell">{humidity?.replace('%', '') ?? '-'}</td>
									<td className="unit-cell">%</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Pressure Section */}
			<div className="dashboard-section pressure-section">
				<div className="section-header">Pressure</div>
				<div className="section-content">
					<div className="table-responsive">
						<table className="dashboard-table no-header">
							<tbody>
								<tr>
									<td>Station</td>
									<td className="value-cell">{stationPressure}</td>
									<td className="value-cell">{stationPressureMb}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MWSDashboard;
